import { useEffect, useState } from "react";
import axios from "axios";
import { Copy, Check, KeyRound, Loader2, LogOut, Plus } from "lucide-react";
import { AdminSupport } from "@/components/AdminSupport";
import { AdminPromos } from "@/components/AdminPromos";
import { AdminLeads } from "@/components/AdminLeads";
import { AdminAnnounce } from "@/components/AdminAnnounce";

const TOKEN = "kk_admin_token";
const admin = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/admin` });
admin.interceptors.request.use((c) => {
  const t = localStorage.getItem(TOKEN);
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
const errText = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};
const field = "w-full bg-black border border-[#2a2f3d] px-3 py-2 text-sm focus:border-[#e69d27] outline-none";

function Login({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const { data } = await admin.post("/login", { email, password });
      localStorage.setItem(TOKEN, data.token); onDone(data.email);
    } catch (x) { setErr(errText(x)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="max-w-sm mx-auto mt-24 space-y-4 border border-[#2a2f3d] bg-[#0e1013] p-6" data-testid="admin-login-form">
      <h1 className="font-display uppercase text-3xl flex items-center gap-2"><KeyRound className="text-[#e69d27]" /> Licence admin</h1>
      <input className={field} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
      <input className={field} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
      {err && <p className="text-xs text-[#ff453a]" data-testid="admin-login-error">{err}</p>}
      <button disabled={busy} className="btn-amber w-full px-4 py-2 text-sm flex items-center justify-center gap-2" data-testid="admin-login-submit-btn">
        {busy && <Loader2 className="spin" size={14} />} Log in
      </button>
    </form>
  );
}

function CreateForm({ onCreated }) {
  const [label, setLabel] = useState("");
  const [email, setEmail] = useState("");
  const [minutes, setMinutes] = useState(60);
  const [product, setProduct] = useState("genalpha");
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    try {
      const { data } = await admin.post("/licences", { label, email, monthly_minutes: Number(minutes), product });
      setLabel(""); setEmail(""); onCreated(data);
    } catch (x) { setErr(errText(x)); }
  };
  const testRecast = async () => {
    setErr(null);
    try { const { data } = await admin.post("/licences/test-recast"); onCreated(data); } catch (x) { setErr(errText(x)); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_2fr_1fr_auto] gap-2 items-end" data-testid="admin-create-form">
      <label className="text-xs text-[#9ba3b4]">Product<select className={field} value={product} onChange={(e) => setProduct(e.target.value)} data-testid="admin-create-product-select"><option value="genalpha">Gen Alpha</option><option value="denoise">Denoise</option><option value="skin">Skin</option><option value="texture">Texture</option><option value="reactor">Reactor</option><option value="deflicker">Deflicker</option><option value="recast">Recast</option></select></label>
      <label className="text-xs text-[#9ba3b4]">Customer / label<input className={field} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Studio Rouge" data-testid="admin-create-label-input" /></label>
      <label className="text-xs text-[#9ba3b4]">Email (optional)<input className={field} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-create-email-input" /></label>
      <label className="text-xs text-[#9ba3b4]">Minutes / month<input className={field} type="number" min={1} value={minutes} onChange={(e) => setMinutes(e.target.value)} data-testid="admin-create-minutes-input" /></label>
      <button className="btn-amber px-4 py-2 text-sm flex items-center gap-2 h-[38px]" data-testid="admin-create-submit-btn"><Plus size={14} /> Create key</button>
      {err && <p className="text-xs text-[#ff453a] md:col-span-5" data-testid="admin-create-error">{err}</p>}
      <div className="md:col-span-5">
        <button type="button" onClick={testRecast} className="btn-ghost px-3 py-1 text-xs flex items-center gap-1" data-testid="admin-test-recast-btn">
          <Plus size={12} /> Issue myself a test Recast key
        </button>
      </div>
    </form>
  );
}

function NewKey({ lic, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(lic.key); setCopied(true); };
  return (
    <div className="border border-[#e69d27] bg-[#e69d27]/10 p-4 space-y-2" data-testid="admin-new-key">
      <p className="text-sm">New key for <b>{lic.label}</b>. Copy it now, it won't be shown again.</p>
      <div className="flex items-center gap-2">
        <code className="font-mono text-lg tracking-wider text-[#e69d27]" data-testid="admin-new-key-value">{lic.key}</code>
        <button onClick={copy} className="btn-ghost px-3 py-1 text-xs flex items-center gap-1" data-testid="admin-new-key-copy-btn">
          {copied ? <Check size={12} /> : <Copy size={12} />}{copied ? "copied" : "copy"}
        </button>
        <button onClick={onClose} className="btn-ghost px-3 py-1 text-xs ml-auto" data-testid="admin-new-key-close-btn">done</button>
      </div>
    </div>
  );
}

function Row({ lic, onUpdate }) {
  const [minutes, setMinutes] = useState(lic.monthly_minutes);
  const patch = async (body) => { const { data } = await admin.patch(`/licences/${lic.id}`, body); onUpdate(data); };
  const revoked = lic.status !== "active";
  return (
    <tr className={`border-t border-[#2a2f3d] ${revoked ? "opacity-50" : ""}`} data-testid={`admin-licence-row-${lic.key_hint}`}>
      <td className="py-2 pr-3">{lic.label}<div className="text-[11px] text-[#5c6479]">{lic.email}</div>
        <div className="text-[10px] uppercase tracking-widest text-[#9ba3b4]" data-testid={`admin-licence-product-${lic.key_hint}`}>{lic.product} · {lic.source}{lic.product !== "genalpha" ? ` · ${lic.activations}/3 machines` : ""}</div></td>
      <td className="pr-3 font-mono">•••{lic.key_hint}</td>
      <td className="pr-3 font-mono" data-testid={`admin-licence-usage-${lic.key_hint}`}>{lic.used_minutes} / {lic.monthly_minutes} min</td>
      <td className="pr-3">
        <div className="flex gap-1">
          <input type="number" min={1} value={minutes} onChange={(e) => setMinutes(e.target.value)} className="w-20 bg-black border border-[#2a2f3d] px-2 py-1 text-xs" data-testid={`admin-licence-minutes-input-${lic.key_hint}`} />
          <button onClick={() => patch({ monthly_minutes: Number(minutes) })} className="btn-ghost px-2 py-1 text-xs" data-testid={`admin-licence-save-btn-${lic.key_hint}`}>save</button>
        </div>
      </td>
      <td className="pr-3 uppercase text-xs" data-testid={`admin-licence-status-${lic.key_hint}`}>{lic.status}</td>
      <td className="text-right">
        <button onClick={() => patch({ status: revoked ? "active" : "revoked" })} className="btn-ghost px-3 py-1 text-xs" data-testid={`admin-licence-toggle-btn-${lic.key_hint}`}>
          {revoked ? "reactivate" : "revoke"}
        </button>
      </td>
    </tr>
  );
}

export default function AdminPage() {
  const [me, setMe] = useState(undefined);
  const [list, setList] = useState([]);
  const [fresh, setFresh] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN)) { setMe(null); return; }
    admin.get("/me").then((r) => setMe(r.data.email)).catch(() => { localStorage.removeItem(TOKEN); setMe(null); });
  }, []);
  useEffect(() => { if (me) admin.get("/licences").then((r) => setList(r.data)).catch(() => setMe(null)); }, [me]);

  const logout = () => { localStorage.removeItem(TOKEN); setMe(null); };
  const update = (d) => setList((l) => l.map((x) => (x.id === d.id ? d : x)));

  return (
    <div className="min-h-screen bg-[#0b0c0e] text-white px-6 py-8" data-testid="admin-page">
      {me === undefined && <Loader2 className="spin mx-auto mt-24 text-[#e69d27]" />}
      {me === null && <Login onDone={setMe} />}
      {me && (
        <div className="max-w-[1100px] mx-auto space-y-6">
          <div className="flex items-center gap-3">
            <h1 className="font-display uppercase text-3xl">KolorKlinic licences</h1>
            <span className="ml-auto font-mono text-xs text-[#9ba3b4]">{me}</span>
            <button onClick={logout} className="btn-ghost px-3 py-1 text-xs flex items-center gap-1" data-testid="admin-logout-btn"><LogOut size={12} /> log out</button>
          </div>
          <CreateForm onCreated={(d) => { setFresh(d); setList((l) => [d, ...l]); }} />
          {fresh && <NewKey lic={fresh} onClose={() => setFresh(null)} />}
          <table className="w-full text-sm" data-testid="admin-licence-table">
            <thead className="text-left font-mono text-[11px] uppercase tracking-widest text-[#5c6479]">
              <tr><th className="py-2">Customer</th><th>Key</th><th>Used this month</th><th>Monthly limit</th><th>Status</th><th /></tr>
            </thead>
            <tbody>{list.map((l) => <Row key={l.id} lic={l} onUpdate={update} />)}</tbody>
          </table>
          {list.length === 0 && <p className="text-sm text-[#5c6479]" data-testid="admin-empty">No keys yet. Create the first one above.</p>}
          <AdminAnnounce admin={admin} me={me} />
          <AdminLeads admin={admin} />
          <AdminPromos admin={admin} />
          <AdminSupport admin={admin} />
        </div>
      )}
    </div>
  );
}
